import jsPDF from 'jspdf';
import { format } from 'date-fns';
import { Employee } from '../types';

// Validation function to check if employee has all required data
export interface ValidationResult {
  isValid: boolean;
  missingFields: string[];
}

export function validateEmployeeForAppointmentLetter(employee: Employee): ValidationResult {
  const missingFields: string[] = [];

  // Required fields
  if (!employee.name) missingFields.push('Employee Name');
  if (!employee.permanentAddress) missingFields.push('Permanent Address');
  if (!employee.city) missingFields.push('City');
  if (!employee.state) missingFields.push('State');
  if (!employee.pincode) missingFields.push('PIN Code');
  if (!employee.designation) missingFields.push('Designation');
  if (!employee.dateOfJoining) missingFields.push('Date of Joining');

  // Salary components are REQUIRED
  if (!employee.salaryComponents || employee.salaryComponents.length === 0) {
    missingFields.push('Salary Components (Please add at least one salary component)');
  }

  // Optional but recommended
  if (!employee.locationNames || employee.locationNames.length === 0) {
    missingFields.push('Office Location (Recommended)');
  }

  return {
    isValid: missingFields.length === 0,
    missingFields
  };
}

export interface AppointmentLetterOptions {
  employee: Employee;
  organizationName: string;
  officeName?: string;
  salaryType?: 'ctc' | 'inhand'; // 'ctc' for CTC version, 'inhand' for In-Hand version
  letterDate?: Date; // Custom date for the letter (defaults to today)
}

export function generateAppointmentLetter(options: AppointmentLetterOptions): void {
  const { employee, officeName, salaryType = 'ctc', letterDate = new Date() } = options;

  // Company name constant - used throughout the letter
  const companyName = 'CARECRES Services India Pvt. Ltd.';
  const companyNameFull = 'CARECRES SERVICES INDIA PRIVATE LIMITED';

  // Calculate salary totals
  const salaryComponents = employee.salaryComponents || [];
  const grossCTC = salaryComponents.reduce((sum, c) => sum + c.amount, 0);

  // Define which components to exclude from In-Hand earnings
  // These are employer contributions that don't come to employee's hand
  const inHandDeductionComponent = 'Statutory Deductions (In-Hand)';
  const excludedFromInHand = ['PF', 'ESIC', 'EC Policy', 'Statutory Deductions (In-Hand)'];

  // For In-Hand version: earnings exclude PF, ESIC, EC Policy, and Statutory Deductions
  const earningsForInHand = salaryComponents.filter(c => !excludedFromInHand.includes(c.component));
  const statutoryDeduction = salaryComponents.find(c => c.component === inHandDeductionComponent);

  // Calculate totals for In-Hand
  const totalEarningsForInHand = earningsForInHand.reduce((sum, c) => sum + c.amount, 0);
  const statutoryDeductionAmount = statutoryDeduction?.amount || 0;
  const netInHand = totalEarningsForInHand - statutoryDeductionAmount;

  // Determine which salary amount to show based on salaryType
  const displaySalary = salaryType === 'inhand' ? netInHand : grossCTC;
  const salaryLabel = salaryType === 'inhand' ? 'In-Hand Salary' : 'CTC (Cost to Company)';

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let yPosition = 15;

  // Colors - Simple black text
  const textColor = '#000000';
  const brandBlue = '#0066FF';

  // Determine gender prefix
  const genderPrefix = employee.gender === 'Female' ? 'Ms.' : 'Mr.';
  const appointmentDate = employee.dateOfJoining ? new Date(employee.dateOfJoining) : new Date();

  // Helper to add simple header with company logo
  const addCompanyHeader = () => {
    // Company name text in blue
    doc.setTextColor(41, 128, 185); // Professional blue color
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('CARECRES', margin, yPosition + 8);

    yPosition += 18;
  };

  // Helper to add simple footer
  const addFooter = () => {
    const footerY = pageHeight - 20;
    const centerX = pageWidth / 2;

    // Company contact info in blue
    doc.setTextColor(41, 128, 185); // Professional blue color
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    // Phone on first line (centered)
    doc.text('+91 22 4127 2610', centerX, footerY, { align: 'center' });

    // Company name (centered, bold, all caps)
    doc.setFont('helvetica', 'bold');
    doc.text('CARECRES SERVICES INDIA PVT. LTD.', centerX, footerY + 5, { align: 'center' });

    // Address (centered)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const address = '449, A Wing, Balaji Bhavan, Sector 11, Plot No. 42A, CBD Belapur, Navi Mumbai, 400614';
    doc.text(address, centerX, footerY + 9, { align: 'center' });
  };

  // PAGE 1 - Appointment Letter
  addCompanyHeader();

  // Date (right aligned) - use letterDate parameter
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(format(letterDate, 'dd/MM/yyyy'), pageWidth - margin, yPosition, { align: 'right' });
  yPosition += 8;

  // Candidate info
  doc.text(employee.name, margin, yPosition);
  yPosition += 5;

  if (employee.permanentAddress) {
    const addressParts = [
      employee.permanentAddress,
      employee.city,
      employee.state,
      employee.pincode
    ].filter(Boolean).join(', ');

    const addressLines = doc.splitTextToSize(addressParts, contentWidth * 0.6);
    doc.text(addressLines, margin, yPosition);
    yPosition += addressLines.length * 5 + 8;
  }

  // Salutation
  doc.text(`Dear ${employee.name},`, margin, yPosition);
  yPosition += 10;

  // Subject - bold with designation on same line
  doc.setFont('helvetica', 'bold');
  const subjectText = `Subject: Appointment Letter For The Position Of ${employee.designation || 'Staff'}`;
  doc.text(subjectText, margin, yPosition);
  yPosition += 10;

  // Opening paragraph
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const openingText = `We at ${companyName} invite you to join us as ${employee.designation || 'Staff'} at our ${officeName || 'office'}. Your association with the organization will be greatly appreciated, and we would like to see your qualities produce beneficial results.`;
  const openingLines = doc.splitTextToSize(openingText, contentWidth);
  doc.text(openingLines, margin, yPosition);
  yPosition += openingLines.length * 5 + 8;

  // Terms and conditions - maintaining original content
  const terms = [
    {
      title: '1. Position & Reporting',
      content: `Your designation shall be ${employee.designation || 'Staff'}, and you will report to the ${employee.reportingManager || 'Supervisor'} or any person authorized by the management.`
    },
    {
      title: '2. Place of Work',
      content: `You will be deployed at ${officeName || 'our office'}. However, based on business requirements, you may be transferred to any other site of the company or its clients.`
    },
    {
      title: '3. Compensation',
      content: `Your ${salaryLabel} will be Rs.${displaySalary.toLocaleString('en-IN')}/- (Rupees ${convertNumberToWords(displaySalary)} Only).`
    },
    {
      title: '4. Probation Period',
      content: `You will be on probation for ${employee.probationPeriod || 3} months from the date of joining. After successful completion, your employment may be confirmed in writing.`
    },
    {
      title: '5. Conduct & Discipline',
      content: 'You are required to maintain good conduct, discipline, punctuality, and follow all company rules, regulations, and safety guidelines.'
    },
    {
      title: '6. Termination Clause',
      content: 'During probation, either party may terminate employment by giving 7 days\' notice or salary in lieu. After confirmation, employment may be terminated with 30 days\' notice or salary in lieu, subject to company policy.'
    },
    {
      title: '7. Confidentiality',
      content: 'You shall maintain confidentiality of all company and client information and will not disclose it to any third party.'
    }
  ];

  terms.forEach((term) => {
    // Check if we need a new page
    if (yPosition > pageHeight - 60) {
      addFooter();
      doc.addPage();
      yPosition = margin;
    }

    // Term title (bold)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(term.title, margin, yPosition);
    yPosition += 6;

    // Term content
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const contentLines = doc.splitTextToSize(term.content, contentWidth);
    doc.text(contentLines, margin, yPosition);
    yPosition += contentLines.length * 5 + 6;
  });

  // Acceptance section
  if (yPosition > pageHeight - 80) {
    addFooter();
    doc.addPage();
    yPosition = margin;
  }

  yPosition += 5;

  // Acceptance header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text('Acceptance', margin, yPosition);
  yPosition += 8;

  doc.setFont('helvetica', 'normal');
  const acceptanceText = `If you accept the above terms, please sign a copy of this letter and return to us as formal confirmation of your acceptance.`;
  const acceptanceLines = doc.splitTextToSize(acceptanceText, contentWidth);
  doc.text(acceptanceLines, margin, yPosition);
  yPosition += acceptanceLines.length * 5 + 6;

  const welcomeText = `Welcome to ${companyName}, and we are thrilled to have you on board. A copy of this letter, as an acknowledgement of its acceptance, please sign and hand back.`;
  const welcomeLines = doc.splitTextToSize(welcomeText, contentWidth);
  doc.text(welcomeLines, margin, yPosition);
  yPosition += welcomeLines.length * 5 + 12;

  // Signature section
  doc.text('Yours sincerely,', margin, yPosition);
  yPosition += 5;

  doc.setFont('helvetica', 'normal');
  doc.text('CARECRES SERVICES INDIA PVT. LTD.', margin, yPosition);
  yPosition += 20; // Blank space for signature

  doc.text('Authorized Signatory', margin, yPosition);

  addFooter();

  // PAGE 2 - Salary Structure
  doc.addPage();
  yPosition = margin;

  addCompanyHeader();

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text('Salary Structure', margin, yPosition);
  yPosition += 10;

  // Employee details
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Employee Name:', margin, yPosition);
  doc.setFont('helvetica', 'normal');
  doc.text(employee.name, margin + 40, yPosition);
  yPosition += 6;

  doc.setFont('helvetica', 'bold');
  doc.text('Designation:', margin, yPosition);
  doc.setFont('helvetica', 'normal');
  doc.text(employee.designation || 'Staff', margin + 40, yPosition);
  yPosition += 6;

  doc.setFont('helvetica', 'bold');
  doc.text('Date of Joining:', margin, yPosition);
  doc.setFont('helvetica', 'normal');
  doc.text(format(appointmentDate, 'dd/MM/yyyy'), margin + 40, yPosition);
  yPosition += 12;

  // Salary table - simple borders
  // Table header
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  doc.rect(margin, yPosition, contentWidth, 10);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Component', margin + 2, yPosition + 7);
  doc.text('Amount', pageWidth - margin - 2, yPosition + 7, { align: 'right' });
  yPosition += 10;

  // Salary components based on salary type
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);

  if (salaryType === 'inhand') {
    // In-Hand version: Show Earnings, then Statutory Deduction, then Net In-Hand

    // Earnings section header
    doc.setFont('helvetica', 'bold');
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPosition, contentWidth, 8, 'F');
    doc.rect(margin, yPosition, contentWidth, 8);
    doc.text('Earnings', margin + 2, yPosition + 6);
    yPosition += 8;

    // Earnings components (all except statutory deduction)
    doc.setFont('helvetica', 'normal');
    earningsForInHand.forEach((component) => {
      doc.rect(margin, yPosition, contentWidth, 8);
      doc.text(component.component, margin + 2, yPosition + 6);
      doc.text(component.amount.toLocaleString('en-IN'), pageWidth - margin - 2, yPosition + 6, { align: 'right' });
      yPosition += 8;
    });

    // Total Earnings row
    doc.setFont('helvetica', 'bold');
    doc.rect(margin, yPosition, contentWidth, 8);
    doc.text('Total Earnings', margin + 2, yPosition + 6);
    doc.text(totalEarningsForInHand.toLocaleString('en-IN'), pageWidth - margin - 2, yPosition + 6, { align: 'right' });
    yPosition += 8;

    // Statutory Deductions section (only if the component exists)
    if (statutoryDeduction) {
      // Show statutory deduction as single line with bold styling
      doc.setFont('helvetica', 'bold');
      doc.setFillColor(250, 240, 240);
      doc.rect(margin, yPosition, contentWidth, 8, 'F');
      doc.rect(margin, yPosition, contentWidth, 8);
      doc.text('Statutory Deductions', margin + 2, yPosition + 6);
      doc.text(`- ${statutoryDeductionAmount.toLocaleString('en-IN')}`, pageWidth - margin - 2, yPosition + 6, { align: 'right' });
      yPosition += 8;
    }

    // Net In-Hand Salary row
    doc.setFont('helvetica', 'bold');
    doc.setFillColor(220, 240, 220);
    doc.rect(margin, yPosition, contentWidth, 10, 'F');
    doc.rect(margin, yPosition, contentWidth, 10);
    doc.text('Net In-Hand Salary (Monthly)', margin + 2, yPosition + 7);
    doc.text(netInHand.toLocaleString('en-IN'), pageWidth - margin - 2, yPosition + 7, { align: 'right' });
    yPosition += 15;

  } else {
    // CTC version: Show all components and Gross CTC
    salaryComponents.forEach((component) => {
      doc.rect(margin, yPosition, contentWidth, 8);
      doc.text(component.component, margin + 2, yPosition + 6);
      doc.text(component.amount.toLocaleString('en-IN'), pageWidth - margin - 2, yPosition + 6, { align: 'right' });
      yPosition += 8;
    });

    // Total/Gross CTC row
    doc.setFont('helvetica', 'bold');
    doc.rect(margin, yPosition, contentWidth, 10);
    doc.text('Gross CTC (Monthly)', margin + 2, yPosition + 7);
    doc.text(grossCTC.toLocaleString('en-IN'), pageWidth - margin - 2, yPosition + 7, { align: 'right' });
    yPosition += 15;

    // Note about deductions
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    const deductionNote = 'Note: Statutory deductions (PF, ESIC, Professional Tax, etc.) will be made as applicable. The net in-hand salary may vary based on these deductions.';
    const deductionLines = doc.splitTextToSize(deductionNote, contentWidth);
    doc.text(deductionLines, margin, yPosition);
    yPosition += deductionLines.length * 5 + 10;
  }

  yPosition += 5;

  // Employee acceptance section
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Employee Acceptance', margin, yPosition);
  yPosition += 8;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('I hereby accept the terms and conditions mentioned in this appointment letter.', margin, yPosition);
  yPosition += 15;

  // Signature fields
  doc.setFont('helvetica', 'bold');
  doc.text('Employee Name:', margin, yPosition);
  doc.setFont('helvetica', 'normal');
  doc.text(employee.name, margin + 40, yPosition);
  yPosition += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Signature:', margin, yPosition);
  doc.setFont('helvetica', 'normal');
  doc.text('_______________________________', margin + 40, yPosition);
  yPosition += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Date:', margin, yPosition);
  doc.setFont('helvetica', 'normal');
  doc.text('_______________________________', margin + 40, yPosition);

  addFooter();

  // Save the PDF
  const salaryTypeLabel = salaryType === 'inhand' ? 'InHand' : 'CTC';
  const fileName = `Appointment_Letter_${employee.name.replace(/\s+/g, '_')}_${salaryTypeLabel}_${format(new Date(), 'yyyyMMdd')}.pdf`;
  doc.save(fileName);
}

// Helper function to convert number to words (Indian numbering system)
function convertNumberToWords(num: number): string {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

  if (num === 0) return 'Zero';

  let words = '';

  // Lakhs
  if (num >= 100000) {
    const lakhs = Math.floor(num / 100000);
    words += convertNumberToWords(lakhs) + ' Lakh ';
    num %= 100000;
  }

  // Thousands
  if (num >= 1000) {
    const thousands = Math.floor(num / 1000);
    words += convertNumberToWords(thousands) + ' Thousand ';
    num %= 1000;
  }

  // Hundreds
  if (num >= 100) {
    words += ones[Math.floor(num / 100)] + ' Hundred ';
    num %= 100;
  }

  // Tens and ones
  if (num >= 20) {
    words += tens[Math.floor(num / 10)] + ' ';
    num %= 10;
  } else if (num >= 10) {
    words += teens[num - 10] + ' ';
    return words.trim();
  }

  if (num > 0) {
    words += ones[num] + ' ';
  }

  return words.trim();
}
